import { PARTNER_MSG, PARTNER_PICKER_MSG, compareCodes, formatZip, paymentTermsLabel, type Partner, type PartnerList, type PartnerSummary, type PaymentTerms } from "@genbaryoku/contract";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { api, messageOf } from "../api.js";
import { usePageName } from "../components/BackButton.jsx";
import { Icon } from "../components/Shell.jsx";
import { ErrorText, Help, useFieldIds, useToast } from "../components/ui.jsx";
import { useUses } from "../core/uses.js";
import { useMe } from "../auth.js";
import { hasModule } from "../core/slots.jsx";
import { useLabels } from "../labels.js";

import { ymdShort } from "../lib/dates.js";
/** 「26/11/05」（#320 ★8＝画面の年月日は年2桁。日本時間の日） */
export const fmtDate = (iso: string) => ymdShort(iso);

/**
 * 顧客の一覧（縦串2a → 縦串9 028 → 2026-09-23 登録は別の画面 /partners/new へ。言葉は会社の設定 027：顧客／得意先／お客様…）。
 * docs/17 §2「顧客 ＞ 案件 ＞ 工事 ＞ 見積」の入口。一覧が上、右上に「{customer}を登録」
 * 9/29（#92、docs/35 決め6）：まだ1件も無いときは「まだ{customer}はありません。」の1文と主役のボタン（右上のボタンはそのあいだ出さない）。
 *   {vendor}・「使わない」の説明は見出しの右の「？」へ
 */
export function PartnersPage() {
  const ids = useFieldIds(["q"] as const);
  const L = useLabels();
  const can = useUses();
  /**
   * 「顧客｜外注先｜パートナー」の切り替え（047、docs/33 決めごと3。#213 でパートナー＝パートナー手数料の会社だけ）。URL の ?type=vendor／referrer で覚える。
   * 両方の会社はどちらにも出る。パートナーは種別を問わず「パートナー」の印の付いた取引先（顧客・外注先と重なってよい）
   */
  const [sp, setSp] = useSearchParams();
  const me = useMe();
  const referralOn = hasModule(me.data, "referral");
  const view: "client" | "vendor" | "referrer" = sp.get("type") === "vendor" ? "vendor" : sp.get("type") === "referrer" && referralOn ? "referrer" : "client";
  const vendor = view === "vendor";
  const referrer = view === "referrer";
  const setView = (v: "client" | "vendor" | "referrer") => {
    const next = new URLSearchParams(sp);
    if (v === "client") next.delete("type");
    else next.set("type", v);
    setSp(next, { replace: true });
  };
  const setVendor = (v: boolean) => setView(v ? "vendor" : "client");
  const who = referrer ? L.referrer : vendor ? L.vendor : L.customer;
  usePageName(who);
  const [archived, setArchived] = useState(false);
  const qc = useQueryClient();
  const { toast, node } = useToast();
  const [q, setQ] = useState("");
  const list = useQuery({
    queryKey: ["partners", q, archived ? "archived" : "active", view],
    queryFn: () => api<PartnerList>("GET", `/partners?${view !== "client" ? `type=${view}&` : ""}${archived ? "archived=1&" : ""}${q ? `q=${encodeURIComponent(q)}` : ""}`),
  });
  const unarchive = useMutation({
    mutationFn: (p: PartnerSummary) => api<Partner>("POST", `/partners/${p.id}/unarchive`),
    onSuccess: (p) => {
      toast(`${p.name} ${p.honorific} を戻しました。`);
      void qc.invalidateQueries({ queryKey: ["partners"] });
    },
    onError: (e) => toast(messageOf(e), undefined, "error"),
  });

  // #288：コードの列（顧客は顧客コード・仕入先は仕入先コード）。1件でもあれば列を出し、コードの自然順（2 → 10 → A-1。無いものは後ろでフリガナ順のまま）
  const codeOf = (p: PartnerSummary) => (vendor ? p.vendorCode : referrer ? p.customerCode || p.vendorCode : p.customerCode);
  const rows = list.data?.items ?? [];
  const showCode = rows.some((p) => !!codeOf(p));
  const sorted = showCode ? [...rows].sort((a, b) => compareCodes(codeOf(a), codeOf(b))) : rows;
  const codeLabel = vendor ? PARTNER_PICKER_MSG.vendorCode : PARTNER_PICKER_MSG.customerCode;
  /** 1件も無い（探していない・「使わない」を見ていない）。このときは主役のボタンを空の枠の中に出す */
  const emptyAll = !!list.data && list.data.items.length === 0 && !q && !archived;
  const help = [
    vendor ? `${L.vendor}＝見積をもらう相手。同じ会社が${L.customer}でも${L.vendor}でもあるときは1件で持ちます（「両方」）。${can("vendorQuote") ? `${L.vendorQuote}の取り込みは${L.project}の画面から。` : ""}` : "",
    referrer ? `${L.referrer}＝紹介手数料を払う相手。${L.customer}・${L.vendor}の詳細の「${L.referrer}」で登録します（${L.customer}・${L.vendor}と重なってよい）。` : "",
    archived ? "一覧・候補には出ませんが、出した見積・請求書は残っています。「復元」でまた使えます。" : "",
  ].join("");
  const addBtn = (
    <Link className="btn primary" to={vendor ? "/partners/new?type=vendor" : "/partners/new"}>
      <Icon k="plus" />
      {who}登録
    </Link>
  );
  return (
    <>
      <div className="hrow">
        <h1 data-testid="partners-title">{who}</h1>
        {help ? <Help testId="help-partners" label={`${who}の説明`} text={help} /> : null}
        <div className="seg" role="group" aria-label={`${L.customer}／${L.vendor}${referralOn ? `／${L.referrer}` : ""}`} data-testid="partner-seg">
          <button type="button" aria-pressed={view === "client"} onClick={() => setVendor(false)}>{L.customer}</button>
          <button type="button" aria-pressed={vendor} onClick={() => setVendor(true)}>{L.vendor}</button>
          {referralOn ? <button type="button" aria-pressed={referrer} onClick={() => setView("referrer")} data-testid="partner-seg-referrer">{L.referrer}</button> : null}
        </div>
        <span className="sp" />
        <button type="button" className="btn text" onClick={() => setArchived((v) => !v)}>
          {archived ? `使う${who}表示` : `「使わない」${who}表示`}
        </button>
        {!archived && !emptyAll && !referrer ? addBtn : null}
      </div>

      {archived ? <p className="hint" style={{ margin: "0 0 12px" }}>「使わない」にした{who}です。</p> : null}

      <div className="row" style={{ marginBottom: 10 }}>
        <label htmlFor={ids.q} className="hint">名前で探す</label>
        <input id={ids.q} className="inp" type="search" style={{ width: 320 }} placeholder="名前・略称・フリガナ・コード・法人番号の一部" value={q} onChange={(e) => setQ(e.target.value)} />
        <span className="hint">{list.data ? `${list.data.items.length}件` : ""}</span>
      </div>

      {list.isError ? (
        <ErrorText>{messageOf(list.error)}</ErrorText>
      ) : list.data && list.data.items.length === 0 ? (
        <div className="paper" style={{ padding: "28px 24px" }} data-testid="partners-empty">
          <p style={{ margin: 0, fontSize: "var(--fs-19)", fontWeight: 700 }}>{archived ? `「使わない」にした${who}はありません` : q ? `その名前の${who}はありません` : `まだ${who}はありません。`}</p>
          {q && !archived ? <p className="hint" style={{ margin: "6px 0 0" }}>{`別の言葉で探すか、「${who}を登録」から登録してください。`}</p> : null}
          {emptyAll && !referrer ? <div style={{ marginTop: 16 }}>{addBtn}</div> : null}
        </div>
      ) : (
        <div className="tbl-wrap">
          <table className="list">
            <thead>
              <tr>
                {showCode ? <th data-testid="partners-code-col">{codeLabel}</th> : null}
                <th>名前</th>
                <th>電話</th>
                <th>担当者</th>
                <th>支払条件（請求書のお支払期限）</th>
                <th>登録日</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((p) => (
                <tr key={p.id}>
                  {showCode ? <td className="code" data-testid="partners-code">{codeOf(p)}</td> : null}
                  <td>
                    <Link to={`/partners/${p.id}`} style={{ color: "inherit" }}>
                      <b>{p.name}</b>
                    </Link>
                    {p.partnerType === "both" ? <span className="chip" style={{ marginLeft: 8 }} data-testid="partner-both">{L.customer}・{L.vendor}の両方</span> : null}
                    {referralOn && p.isReferrer ? <span className="chip refmark" style={{ marginLeft: 8 }} data-testid="partner-referrer-chip">{L.referrer} {Math.round(p.referralPct * 100) / 100}%</span> : null}
                    {p.address || p.postalCode ? <div className="muted" style={{ fontSize: 14 }}>{[p.postalCode ? `〒${formatZip(p.postalCode)}` : "", p.address, p.building].filter(Boolean).join(" ")}</div> : null}
                    {p.branchCount ? <div className="muted" style={{ fontSize: 14 }}>支店・営業所 {p.branchCount}</div> : null}
                  </td>
                  <td className="num">{p.phone || <span className="muted">—</span>}</td>
                  <td>
                    {p.contact ? (
                      <>
                        {p.contact.name}
                        {p.contact.mobile || p.contact.email ? <div className="muted num" style={{ fontSize: 14 }}>{[p.contact.mobile, p.contact.email].filter(Boolean).join(" ")}</div> : null}
                      </>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td>{p.paymentTerms ? paymentTermsLabel(p.paymentTerms as PaymentTerms) : <span className="muted">末締め・翌月末払い（既定）</span>}</td>
                  <td className="num">{fmtDate(p.createdAt)}</td>
                  <td>
                    {archived ? (
                      <button type="button" className="small" disabled={unarchive.isPending} onClick={() => unarchive.mutate(p)} aria-label={`${p.name} を復元`}>復元</button>
                    ) : (
                      <Link className="small" to={`/partners/${p.id}`} aria-label={`${p.name} の詳細`}>詳細</Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {node}
    </>
  );
}

export { PARTNER_MSG };
